import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, Image as ImageIcon, Film, X, Download, Loader2, Check,
  HardDriveDownload, Link2, RotateCcw, Save, Ban, Crop, EyeOff, Trash2
} from "lucide-react";

import { API, useAuth } from "@/App";
import { openDrivePicker, isDrivePickerConfigured } from "@/utils/drivePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const AX = { withCredentials: true };

const LAYERS = [
  {
    key: "tiled_text",
    label: "Repeating text across the whole frame",
    hint: "Hardest to crop out — the right choice for \"not for media use\".",
  },
  {
    key: "full_logo",
    label: "Large logo across the frame",
    hint: "One big badge spanning the picture. Keep the opacity low — it covers everything under it.",
  },
  {
    key: "custom_text",
    label: "Custom text",
    hint: "Your own line, placed anywhere on the frame. Supports {username}, {filename}, {date}, {time}.",
  },
  {
    key: "diagonal_band",
    label: "Single diagonal band",
    hint: "One large line across the middle. Cleaner, but croppable.",
  },
  {
    key: "corner_logo",
    label: "Logo in a corner",
    hint: "The Shmira Public Safety badge.",
  },
  {
    key: "bottom_banner",
    label: "Bottom banner bar",
    hint: "A strip along the bottom. Very legible, doesn't cover the footage.",
  },
];

const CORNERS = [
  { value: "top-left", label: "Top left" },
  { value: "top-right", label: "Top right" },
  { value: "bottom-left", label: "Bottom left" },
  { value: "bottom-right", label: "Bottom right" },
];

const QUALITY = [
  { value: "maximum", label: "Maximum", hint: "Archival. Largest file." },
  { value: "high", label: "High", hint: "Indistinguishable from the original." },
  { value: "balanced", label: "Balanced", hint: "Near original size, encodes faster." },
];

const EMPTY_EDIT = { crop: null, trim: null, remove_audio: false, regions: [] };

const POSITIONS = [
  { value: "top-left", label: "Top left" },
  { value: "top-center", label: "Top centre" },
  { value: "top-right", label: "Top right" },
  { value: "middle-left", label: "Middle left" },
  { value: "center", label: "Centre" },
  { value: "middle-right", label: "Middle right" },
  { value: "bottom-left", label: "Bottom left" },
  { value: "bottom-center", label: "Bottom centre" },
  { value: "bottom-right", label: "Bottom right" },
];

const EMPTY_LAYERS = {
  tiled_text: {
    enabled: false, text: "NOT FOR MEDIA USE", opacity: 0.18, angle: -30,
    size_pct: 4.5, spacing_pct: 45, color: "#FFFFFF", stroke: true,
  },
  diagonal_band: {
    enabled: false, text: "INTERNAL USE ONLY", opacity: 0.3, angle: -30,
    size_pct: 8, color: "#FFFFFF", stroke: true,
  },
  corner_logo: {
    enabled: false, corner: "bottom-right", scale_pct: 15,
    opacity: 0.85, padding_pct: 2, offset_x_pct: 0, offset_y_pct: 0,
  },
  full_logo: {
    enabled: false, scale_pct: 80, opacity: 0.12, angle: 0, position: "center",
  },
  custom_text: {
    enabled: false, text: "", position: "bottom-right", size_pct: 4,
    opacity: 0.85, padding_pct: 3, color: "#FFFFFF", stroke: true,
  },
  bottom_banner: {
    enabled: false, text: "{filename} — {date}", opacity: 0.55,
    height_pct: 8, text_color: "#FFFFFF", bg_color: "#000000",
  },
};

const fmtBytes = (n) => {
  if (!n) return "—";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const fmtDuration = (s) => {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

const errText = (e, fallback) =>
  e?.response?.data?.detail || e?.message || fallback;

export default function Watermarking() {
  const { user } = useAuth();

  const [source, setSource] = useState(null);
  const [busy, setBusy] = useState(null);      // 'upload' | 'drive' | 'link'
  const [uploadPct, setUploadPct] = useState(0);
  const [driveUrl, setDriveUrl] = useState("");
  const [dragging, setDragging] = useState(false);

  const [presets, setPresets] = useState([]);
  const [layers, setLayers] = useState(EMPTY_LAYERS);
  const [quality, setQuality] = useState("high");
  const [timestamp, setTimestamp] = useState(0);

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const [job, setJob] = useState(null);
  const [saved, setSaved] = useState(false);
  const [tools, setTools] = useState(undefined); // undefined = still checking

  // Edits that change the picture itself, rather than drawing on top of it.
  // All geometry is frame-relative (0..1) so it survives the preview being
  // displayed at a different size than the export.
  const [edit, setEdit] = useState(EMPTY_EDIT);
  const [mode, setMode] = useState(null); // null | 'crop' | 'region'
  const [regionMode, setRegionMode] = useState("blur");
  const [regionShape, setRegionShape] = useState("rect");
  const [drag, setDrag] = useState(null);
  const frameRef = useRef(null);

  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const previewSeq = useRef(0);

  const isImage = source?.kind === "image";
  const anyLayerOn = useMemo(
    () => Object.values(layers).some((l) => l.enabled),
    [layers]
  );

  // ── presets ────────────────────────────────────────────────────────────────
  useEffect(() => {
    axios
      .get(`${API}/app-config/watermark-presets`, AX)
      .then((r) => setPresets(r.data?.presets || []))
      .catch(() => setPresets([]));
  }, []);

  // Watermarking is the only feature that shells out to ffmpeg. If the server
  // doesn't have it, say so up front instead of letting every upload fail with
  // an error that looks like a problem with the file.
  useEffect(() => {
    axios
      .get(`${API}/watermark/diagnostics`, AX)
      .then((r) => setTools(r.data))
      .catch(() => setTools(null));
  }, []);

  const applyPreset = (id) => {
    const preset = presets.find((p) => p.id === id);
    if (!preset) return;
    setLayers({
      ...EMPTY_LAYERS,
      ...Object.fromEntries(
        Object.entries(preset.layers || {}).map(([k, v]) => [
          k, { ...(EMPTY_LAYERS[k] || {}), ...v },
        ])
      ),
    });
  };

  const setLayer = (key, patch) =>
    setLayers((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  // ── crop / redaction drawing ───────────────────────────────────────────────
  // Pointer events rather than mouse events, so dragging works on a phone.
  // Coordinates are normalised against the rendered frame, which is what makes
  // a box drawn on a downscaled preview land in the right place at full size.
  const pointToFrame = (e) => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return null;
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  };

  const MIN_SIDE = 0.02;

  const clampRect = (r) => {
    const w = Math.min(Math.max(r.w, MIN_SIDE), 1);
    const h = Math.min(Math.max(r.h, MIN_SIDE), 1);
    return {
      x: Math.min(Math.max(r.x, 0), 1 - w),
      y: Math.min(Math.max(r.y, 0), 1 - h),
      w, h,
    };
  };

  // One handler set drives three gestures: drawing a new box on empty space,
  // dragging an existing one to reposition it, and pulling its corner to
  // resize. The live geometry is kept local and only written to `edit` on
  // release, so the preview isn't re-rendered on every pointer move.
  const startDraw = (e) => {
    if (!mode) return;
    const p = pointToFrame(e);
    if (!p) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({ kind: "new", x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  };

  const startEdit = (e, kind, target, base) => {
    if (!mode) return;
    e.stopPropagation();          // don't also start drawing a new box
    const p = pointToFrame(e);
    if (!p) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({ kind, target, origin: p, base, rect: base });
  };

  const onFramePointerMove = (e) => {
    if (!drag) return;
    const p = pointToFrame(e);
    if (!p) return;

    if (drag.kind === "new") {
      setDrag((d) => ({ ...d, x1: p.x, y1: p.y }));
      return;
    }

    const dx = p.x - drag.origin.x;
    const dy = p.y - drag.origin.y;
    const next = drag.kind === "move"
      ? { ...drag.base, x: drag.base.x + dx, y: drag.base.y + dy }
      : { ...drag.base, w: drag.base.w + dx, h: drag.base.h + dy };
    setDrag((d) => ({ ...d, rect: clampRect(next) }));
  };

  const onFramePointerUp = () => {
    if (!drag) return;
    const current = drag;
    setDrag(null);

    if (current.kind === "new") {
      const rect = {
        x: Math.min(current.x0, current.x1),
        y: Math.min(current.y0, current.y1),
        w: Math.abs(current.x1 - current.x0),
        h: Math.abs(current.y1 - current.y0),
      };
      // Ignore a stray tap — anything this small is a misclick, not a box.
      if (rect.w < MIN_SIDE || rect.h < MIN_SIDE) return;
      if (mode === "crop") {
        setEdit((p) => ({ ...p, crop: rect }));
      } else if (mode === "region") {
        setEdit((p) => ({
          ...p,
          regions: [...p.regions, {
            ...rect, mode: regionMode, shape: regionShape,
            strength: 60, color: "#000000",
          }],
        }));
      }
      return;
    }

    const rect = current.rect;
    if (current.target === "crop") {
      setEdit((p) => ({ ...p, crop: { ...p.crop, ...rect } }));
    } else {
      setEdit((p) => ({
        ...p,
        regions: p.regions.map((r, i) =>
          i === current.target ? { ...r, ...rect } : r
        ),
      }));
    }
  };

  const pctBox = (r) => ({
    left: `${r.x * 100}%`, top: `${r.y * 100}%`,
    width: `${r.w * 100}%`, height: `${r.h * 100}%`,
  });

  // Geometry to draw for a shape, preferring the in-flight drag over the
  // stored value so it tracks the finger.
  const liveRect = (target, stored) =>
    drag && drag.kind !== "new" && drag.target === target ? drag.rect : stored;

  const dragRect = drag?.kind === "new" && {
    left: `${Math.min(drag.x0, drag.x1) * 100}%`,
    top: `${Math.min(drag.y0, drag.y1) * 100}%`,
    width: `${Math.abs(drag.x1 - drag.x0) * 100}%`,
    height: `${Math.abs(drag.y1 - drag.y0) * 100}%`,
  };

  const setRegion = (index, patch) =>
    setEdit((p) => ({
      ...p,
      regions: p.regions.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    }));

  const removeRegion = (index) =>
    setEdit((p) => ({ ...p, regions: p.regions.filter((_, i) => i !== index) }));

  // ── sources ────────────────────────────────────────────────────────────────
  const adoptSource = (data) => {
    setSource(data);
    setJob(null);
    setSaved(false);
    setPreview(null);
    setPreviewError(null);
    // Default the preview to a frame a little way in — frame zero is often a
    // black lead-in that tells you nothing about how the watermark reads.
    const dur = data?.info?.duration || 0;
    setTimestamp(dur > 2 ? Math.min(dur * 0.1, dur - 0.1) : 0);
    // Crop/trim/regions are geometry for THIS file; carrying them to the next
    // one would silently apply a box drawn on a different picture.
    setEdit(EMPTY_EDIT);
    setMode(null);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy("upload");
    setUploadPct(0);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/watermark/source/upload`, form, {
        ...AX,
        onUploadProgress: (e) =>
          e.total && setUploadPct(Math.round((e.loaded / e.total) * 100)),
      });
      adoptSource(res.data);
      toast.success("Ready to watermark");
    } catch (e) {
      toast.error(errText(e, "Upload failed"));
    } finally {
      setBusy(null);
      setUploadPct(0);
    }
  };

  const pickFromDrive = async () => {
    if (!isDrivePickerConfigured()) {
      toast.error("Google Drive isn't configured on this deployment");
      return;
    }
    try {
      await openDrivePicker({
        onPick: async (docs, token) => {
          const doc = docs?.[0];
          if (!doc) return;
          setBusy("drive");
          try {
            const res = await axios.post(
              `${API}/watermark/source/drive`,
              {
                file_id: doc.id,
                access_token: token,
                name: doc.name,
              },
              AX
            );
            adoptSource(res.data);
            toast.success(`Pulled "${doc.name}" from Drive`);
          } catch (e) {
            toast.error(errText(e, "Couldn't fetch that Drive file"));
          } finally {
            setBusy(null);
          }
        },
      });
    } catch {
      toast.error("Couldn't open the Drive picker");
    }
  };

  const fetchDriveLink = async () => {
    if (!driveUrl.trim()) return;
    setBusy("link");
    try {
      const res = await axios.post(
        `${API}/watermark/source/drive-link`, { url: driveUrl.trim() }, AX
      );
      adoptSource(res.data);
      setDriveUrl("");
      toast.success("Ready to watermark");
    } catch (e) {
      toast.error(errText(e, "Couldn't fetch that link"), { duration: 9000 });
    } finally {
      setBusy(null);
    }
  };

  const clearSource = async () => {
    const id = source?.source_id;
    setSource(null);
    setPreview(null);
    setJob(null);
    setSaved(false);
    setEdit(EMPTY_EDIT);
    setMode(null);
    if (id) {
      axios.delete(`${API}/watermark/sources/${id}`, AX).catch(() => {});
    }
  };

  // ── preview ────────────────────────────────────────────────────────────────
  // Re-rendered whenever the config changes, so what's on screen is always what
  // will be exported. Debounced, and stale responses are dropped, so dragging a
  // slider doesn't paint an out-of-date frame.
  const refreshPreview = useCallback(async () => {
    if (!source) return;
    const seq = ++previewSeq.current;
    setPreviewing(true);
    setPreviewError(null);
    try {
      const res = await axios.post(
        `${API}/watermark/preview`,
        {
          source_id: source.source_id,
          config: { layers, edit },
          timestamp,
          // While the crop box is being positioned we need the FULL frame —
          // a pre-cropped preview would shrink under the cursor and the box
          // could never be dragged back outwards.
          skip_crop: mode === "crop",
        },
        AX
      );
      if (seq === previewSeq.current) setPreview(res.data.image);
    } catch (e) {
      if (seq === previewSeq.current) {
        setPreviewError(errText(e, "Couldn't render the preview"));
      }
    } finally {
      if (seq === previewSeq.current) setPreviewing(false);
    }
  }, [source, layers, timestamp, edit, mode]);

  useEffect(() => {
    if (!source) return undefined;
    const t = setTimeout(refreshPreview, 280);
    return () => clearTimeout(t);
  }, [source, layers, timestamp, edit, mode, refreshPreview]);

  // ── job ────────────────────────────────────────────────────────────────────
  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => stopPolling, []);

  const startExport = async () => {
    if (!source || !anyLayerOn) return;
    setSaved(false);
    try {
      const res = await axios.post(
        `${API}/watermark/jobs`,
        {
          source_id: source.source_id,
          config: { layers, edit },
          quality,
        },
        AX
      );
      setJob(res.data);
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/watermark/jobs/${res.data.job_id}`, AX);
          setJob(s.data);
          if (["done", "error", "cancelled"].includes(s.data.state)) {
            stopPolling();
            if (s.data.state === "error") toast.error(s.data.error || "Export failed");
            if (s.data.state === "done") toast.success("Watermarked file ready");
          }
        } catch (e) {
          stopPolling();
          toast.error(errText(e, "Lost track of that export"));
        }
      }, 1200);
    } catch (e) {
      toast.error(errText(e, "Couldn't start the export"));
    }
  };

  const cancelExport = async () => {
    if (!job) return;
    try {
      await axios.post(`${API}/watermark/jobs/${job.job_id}/cancel`, {}, AX);
      stopPolling();
      setJob((j) => ({ ...j, state: "cancelled" }));
    } catch (e) {
      toast.error(errText(e, "Couldn't cancel"));
    }
  };

  // Fetched as a blob rather than linked, so the request carries the auth
  // header — an <a href> can't, and the endpoint requires a session.
  const download = async () => {
    if (!job?.ready) return;
    try {
      const res = await axios.get(
        `${API}/watermark/jobs/${job.job_id}/download`,
        { ...AX, responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = job.filename || "watermarked";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(errText(e, "Download failed"));
    }
  };

  const saveToLibrary = async () => {
    if (!job?.ready) return;
    try {
      await axios.post(`${API}/watermark/jobs/${job.job_id}/save-to-media`, {}, AX);
      setSaved(true);
      toast.success("Saved to the media library");
    } catch (e) {
      toast.error(errText(e, "Couldn't save to the library"));
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  };

  const running = job && ["queued", "rendering", "encoding"].includes(job.state);

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-100">Watermarking</h1>
        <p className="text-sm text-slate-400 mt-1">
          Burn a watermark into a video or photo. The original is never changed —
          you get a separate copy.
        </p>
      </div>

      {tools && tools.ready === false && (
        <div className="mb-4 rounded-lg border border-red-900 bg-red-950/40 p-4">
          <p className="text-sm font-semibold text-red-200">
            Video tools are missing on the server
          </p>
          <p className="text-xs text-red-300/90 mt-1 leading-relaxed">
            Watermarking needs <code>ffmpeg</code> and <code>ffprobe</code> on the
            backend. Right now{" "}
            {[
              !tools.ffmpeg?.found && "ffmpeg",
              !tools.ffprobe?.found && "ffprobe",
            ].filter(Boolean).join(" and ")}{" "}
            {!tools.ffmpeg?.found && !tools.ffprobe?.found ? "are" : "is"} not
            installed, so uploads will fail. This is a deploy setting, not a
            problem with your file — the service builds with Railpack, so
            ffmpeg comes from <code>deploy.aptPackages</code> in{" "}
            <code>backend/railpack.json</code>. Redeploy the backend after
            changing it. (<code>nixpacks.toml</code> is not read by Railpack.)
          </p>
        </div>
      )}

      {tools?.ready && tools.memory?.limit_mb && tools.memory.limit_mb < 1536 && (
        <div className="mb-4 rounded-lg border border-amber-900 bg-amber-950/30 p-3">
          <p className="text-xs text-amber-200/90 leading-relaxed">
            This server has <strong>{tools.memory.limit_mb} MB</strong> of memory.
            Encoding a 4K video needs roughly 1.5&nbsp;GB, so large videos may fail
            with an out-of-memory error — 1080p and smaller should be fine.
            Raising the service's memory in Railway removes the limit.
          </p>
        </div>
      )}

      {!source ? (
        <SourcePicker
          busy={busy}
          uploadPct={uploadPct}
          dragging={dragging}
          setDragging={setDragging}
          onDrop={onDrop}
          fileRef={fileRef}
          uploadFile={uploadFile}
          pickFromDrive={pickFromDrive}
          driveUrl={driveUrl}
          setDriveUrl={setDriveUrl}
          fetchDriveLink={fetchDriveLink}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-5 items-start">
          {/* Preview */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-800">
              <div className="flex items-center gap-2 min-w-0">
                {isImage ? (
                  <ImageIcon className="w-4 h-4 text-slate-400 shrink-0" />
                ) : (
                  <Film className="w-4 h-4 text-slate-400 shrink-0" />
                )}
                <span className="text-sm text-slate-200 truncate">
                  {source.filename}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={clearSource}>
                <X className="w-4 h-4 mr-1" /> Start over
              </Button>
            </div>

            <div className="relative bg-[#0b1120] flex items-center justify-center min-h-[300px]">
              {preview ? (
                // The wrapper shrink-wraps the image, so percentage-positioned
                // boxes line up with the picture rather than the letterboxing.
                <div ref={frameRef} className="relative inline-block leading-none">
                  <img
                    src={preview}
                    alt="Watermark preview"
                    className="max-w-full max-h-[62vh] object-contain block select-none"
                    draggable="false"
                  />

                  {mode && (
                    <div
                      className="absolute inset-0 cursor-crosshair touch-none"
                      onPointerDown={startDraw}
                      onPointerMove={onFramePointerMove}
                      onPointerUp={onFramePointerUp}
                      onPointerCancel={() => setDrag(null)}
                    />
                  )}

                  {dragRect && (
                    <div
                      style={dragRect}
                      className={`absolute border-2 border-blue-400 bg-blue-400/20 pointer-events-none ${
                        mode === "region" && regionShape === "ellipse" ? "rounded-[50%]" : ""
                      }`}
                    />
                  )}

                  {mode === "crop" && edit.crop && (
                    <ShapeBox
                      rect={liveRect("crop", edit.crop)}
                      className="border-emerald-400 bg-emerald-400/10"
                      label="crop — drag to move, corner to resize"
                      onMove={(e) => startEdit(e, "move", "crop", liveRect("crop", edit.crop))}
                      onResize={(e) => startEdit(e, "resize", "crop", liveRect("crop", edit.crop))}
                      onPointerMove={onFramePointerMove}
                      onPointerUp={onFramePointerUp}
                    />
                  )}

                  {mode === "region" && edit.regions.map((r, i) => (
                    <ShapeBox
                      key={i}
                      rect={liveRect(i, r)}
                      ellipse={r.shape === "ellipse"}
                      className={r.mode === "mask"
                        ? "border-red-400 bg-red-400/20"
                        : "border-amber-400 bg-amber-400/20"}
                      label={`${i + 1}. ${r.mode}${r.shape === "ellipse" ? " ○" : ""}`}
                      onMove={(e) => startEdit(e, "move", i, liveRect(i, r))}
                      onResize={(e) => startEdit(e, "resize", i, liveRect(i, r))}
                      onPointerMove={onFramePointerMove}
                      onPointerUp={onFramePointerUp}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-24 text-slate-500 text-sm">
                  {previewError || "Rendering preview…"}
                </div>
              )}
              {previewing && preview && (
                <div className="absolute top-3 right-3 bg-slate-950/80 rounded px-2 py-1 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin text-slate-300" />
                  <span className="text-[11px] text-slate-300">Updating</span>
                </div>
              )}
              {!anyLayerOn && (
                <div className="absolute bottom-3 left-3 bg-amber-950/80 border border-amber-800 rounded px-3 py-1.5">
                  <span className="text-[11px] text-amber-200">
                    No watermark layers on — tick one to see it here
                  </span>
                </div>
              )}
            </div>

            {!isImage && (source.info?.duration || 0) > 0 && (
              <div className="px-4 py-3 border-t border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-slate-400">
                    Preview frame — check the watermark over a light and a dark scene
                  </Label>
                  <span className="text-xs text-slate-300 tabular-nums">
                    {fmtDuration(timestamp)} / {fmtDuration(source.info.duration)}
                  </span>
                </div>
                <Slider
                  value={[timestamp]}
                  min={0}
                  max={Math.max(0.1, source.info.duration)}
                  step={0.1}
                  onValueChange={([v]) => setTimestamp(v)}
                />
              </div>
            )}

            <div className="px-4 py-2.5 border-t border-slate-800 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400">
              <span>{source.info.width}×{source.info.height}</span>
              {!isImage && <span>{source.info.fps} fps</span>}
              {!isImage && <span>{fmtDuration(source.info.duration)}</span>}
              <span className="uppercase">{source.info.codec}</span>
              {source.info.is_10bit && (
                <Badge variant="outline" className="h-4 text-[10px] px-1.5">10-bit</Badge>
              )}
              {source.info.has_audio && (
                <span>
                  {source.info.audio_copied
                    ? "audio kept as-is"
                    : `audio re-encoded (${source.info.audio_codec} can't go in an MP4)`}
                </span>
              )}
              <span>{fmtBytes(source.info.size)}</span>
            </div>
          </div>

          {/* Controls */}
          <div className="space-y-4">
            {presets.length > 0 && (
              <Panel title="Preset">
                <Select onValueChange={applyPreset}>
                  <SelectTrigger><SelectValue placeholder="Start from a preset…" /></SelectTrigger>
                  <SelectContent>
                    {presets.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-2">
                  Presets are managed by an admin in Settings.
                </p>
              </Panel>
            )}

            <Panel title="Edit the video">
              <EditTools
                isImage={isImage}
                source={source}
                edit={edit}
                setEdit={setEdit}
                mode={mode}
                setMode={setMode}
                regionMode={regionMode}
                setRegionMode={setRegionMode}
                regionShape={regionShape}
                setRegionShape={setRegionShape}
                setRegion={setRegion}
                removeRegion={removeRegion}
              />
            </Panel>

            <Panel title="Watermark layers">
              <div className="space-y-3">
                {LAYERS.map(({ key, label, hint }) => (
                  <LayerBlock
                    key={key}
                    layerKey={key}
                    label={label}
                    hint={hint}
                    value={layers[key]}
                    onChange={(patch) => setLayer(key, patch)}
                  />
                ))}
              </div>
            </Panel>

            <Panel title={isImage ? "Image quality" : "Export quality"}>
              <div className="space-y-2">
                {QUALITY.map((q) => (
                  <label
                    key={q.value}
                    className={`flex items-start gap-3 p-2.5 rounded border cursor-pointer transition-colors ${
                      quality === q.value
                        ? "border-blue-600 bg-blue-950/30"
                        : "border-slate-800 hover:border-slate-700"
                    }`}
                  >
                    <input
                      type="radio"
                      className="mt-1"
                      checked={quality === q.value}
                      onChange={() => setQuality(q.value)}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm text-slate-200">
                        {q.label}
                        {!isImage && source.estimates?.[q.value] ? (
                          <span className="text-slate-500 font-normal">
                            {" "}· about {fmtBytes(source.estimates[q.value])}
                          </span>
                        ) : null}
                      </span>
                      <span className="block text-[11px] text-slate-500">{q.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
              {!isImage && (
                <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                  Adding a watermark means re-encoding the video — the same thing
                  Premiere or Filmora do on export.
                  {source.info.has_audio && source.info.audio_copied
                    ? " Audio is copied untouched."
                    : ""}
                </p>
              )}
            </Panel>

            <div className="space-y-2">
              {running ? (
                <>
                  <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-200 capitalize">
                        {job.state}
                        {job.state === "queued" && " — waiting for a free slot"}
                      </span>
                      <span className="text-sm text-slate-300 tabular-nums">
                        {Math.round(job.percent)}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded overflow-hidden">
                      <div
                        className="h-full bg-blue-600 transition-[width] duration-500"
                        style={{ width: `${Math.max(2, job.percent)}%` }}
                      />
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-500">
                      {job.eta_seconds > 0 && (
                        <span>about {fmtDuration(job.eta_seconds)} remaining</span>
                      )}
                      <span>{fmtDuration(job.elapsed_seconds)} elapsed</span>
                      {/* Shown so a screenshot of a failure is self-explanatory */}
                      {job.resolution && <span>{job.resolution}</span>}
                      {job.source_codec && job.encoder && (
                        <span>{job.source_codec} → {job.encoder}</span>
                      )}
                      {job.attempt === "conservative" && (
                        <span className="text-amber-400">retrying with safer settings</span>
                      )}
                    </div>
                  </div>
                  <Button variant="outline" className="w-full" onClick={cancelExport}>
                    <Ban className="w-4 h-4 mr-2" /> Cancel
                  </Button>
                </>
              ) : job?.ready ? (
                <>
                  <div className="flex items-center gap-2 text-sm text-emerald-400 px-1">
                    <Check className="w-4 h-4" />
                    Ready — {fmtBytes(job.output_size)}
                  </div>
                  <Button className="w-full" onClick={download}>
                    <Download className="w-4 h-4 mr-2" /> Download
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={saveToLibrary}
                    disabled={saved}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saved ? "Saved to library" : "Save to media library"}
                  </Button>
                  <Button variant="ghost" className="w-full" onClick={() => setJob(null)}>
                    <RotateCcw className="w-4 h-4 mr-2" /> Change the watermark
                  </Button>
                </>
              ) : (
                <Button
                  className="w-full"
                  size="lg"
                  onClick={startExport}
                  disabled={!anyLayerOn}
                >
                  {isImage ? "Apply watermark" : "Export watermarked video"}
                </Button>
              )}
              {job?.state === "error" && (
                <p className="text-xs text-red-400 px-1">{job.error}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ShapeBox({
  rect, ellipse, className, label, onMove, onResize, onPointerMove, onPointerUp,
}) {
  return (
    <div
      style={{
        left: `${rect.x * 100}%`, top: `${rect.y * 100}%`,
        width: `${rect.w * 100}%`, height: `${rect.h * 100}%`,
      }}
      // pointer-events must be ON here, and the handlers stopPropagation, so
      // grabbing an existing shape repositions it instead of starting a new box.
      className={`absolute border-2 cursor-move touch-none ${
        ellipse ? "rounded-[50%]" : ""
      } ${className}`}
      onPointerDown={onMove}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      <span className="absolute -top-5 left-0 text-[10px] text-slate-300 whitespace-nowrap pointer-events-none">
        {label}
      </span>
      {/* Resize grip. Sits slightly outside the corner so it stays grabbable
          even when the shape is tiny, and is square on an ellipse because a
          round grip on a round shape is hard to aim at. */}
      <span
        onPointerDown={onResize}
        className="absolute -right-1.5 -bottom-1.5 w-4 h-4 rounded-sm bg-white/90 border border-slate-600 cursor-nwse-resize touch-none"
      />
    </div>
  );
}

function EditTools({
  isImage, source, edit, setEdit, mode, setMode,
  regionMode, setRegionMode, regionShape, setRegionShape,
  setRegion, removeRegion,
}) {
  const duration = source?.info?.duration || 0;
  const trim = edit.trim || { start: 0, end: duration };
  const hasAudio = !!source?.info?.has_audio;

  const setTrim = (patch) => {
    const next = { ...trim, ...patch };
    if (next.end <= next.start) return;
    setEdit((p) => ({
      ...p,
      trim: (next.start <= 0.01 && next.end >= duration - 0.01) ? null : next,
    }));
  };

  return (
    <div className="space-y-4">
      {/* Trim */}
      {!isImage && duration > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-[11px] text-slate-400">Trim start &amp; end</Label>
            <span className="text-[11px] text-slate-300 tabular-nums">
              {fmtDuration(trim.start)} – {fmtDuration(trim.end)}
              {edit.trim && (
                <span className="text-emerald-400">
                  {" "}({fmtDuration(trim.end - trim.start)})
                </span>
              )}
            </span>
          </div>
          <Row label="Start">
            <Slider
              value={[trim.start]} min={0} max={duration} step={0.1}
              onValueChange={([v]) => setTrim({ start: Math.min(v, trim.end - 0.2) })}
              className="flex-1"
            />
          </Row>
          <Row label="End">
            <Slider
              value={[trim.end]} min={0} max={duration} step={0.1}
              onValueChange={([v]) => setTrim({ end: Math.max(v, trim.start + 0.2) })}
              className="flex-1"
            />
          </Row>
          {edit.trim && (
            <Button
              variant="ghost" size="sm" className="mt-1 h-7 text-[11px]"
              onClick={() => setEdit((p) => ({ ...p, trim: null }))}
            >
              Use the whole video
            </Button>
          )}
        </div>
      )}

      {/* Audio */}
      {!isImage && hasAudio && (
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={edit.remove_audio}
            onCheckedChange={(v) => setEdit((p) => ({ ...p, remove_audio: !!v }))}
            className="mt-0.5"
          />
          <span>
            <span className="block text-sm text-slate-200">Remove audio</span>
            <span className="block text-[11px] text-slate-500">
              Exports a silent video. The original keeps its sound.
            </span>
          </span>
        </label>
      )}

      {/* Crop */}
      <div>
        <div className="flex items-center gap-2">
          <Button
            variant={mode === "crop" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode(mode === "crop" ? null : "crop")}
          >
            <Crop className="w-4 h-4 mr-1" />
            {mode === "crop" ? "Done cropping" : "Crop"}
          </Button>
          {edit.crop && (
            <Button
              variant="ghost" size="sm" className="text-[11px]"
              onClick={() => setEdit((p) => ({ ...p, crop: null }))}
            >
              Reset
            </Button>
          )}
        </div>
        {mode === "crop" && (
          <p className="text-[11px] text-slate-500 mt-2">
            Drag a box on the preview to set the crop. The preview shows the
            full frame while you do this so you can drag outwards again.
          </p>
        )}
      </div>

      {/* Blur / privacy mask */}
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={mode === "region" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode(mode === "region" ? null : "region")}
          >
            <EyeOff className="w-4 h-4 mr-1" />
            {mode === "region" ? "Done redacting" : "Blur / mask"}
          </Button>
          {mode === "region" && (
            <>
              <div className="flex rounded border border-slate-700 overflow-hidden">
                {["blur", "mask"].map((m) => (
                  <button
                    key={m}
                    onClick={() => setRegionMode(m)}
                    className={`px-2 py-1 text-[11px] capitalize ${
                      regionMode === m
                        ? "bg-blue-600 text-white"
                        : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <div className="flex rounded border border-slate-700 overflow-hidden">
                {[
                  { value: "rect", label: "Box" },
                  { value: "ellipse", label: "Circle" },
                ].map((sh) => (
                  <button
                    key={sh.value}
                    onClick={() => setRegionShape(sh.value)}
                    className={`px-2 py-1 text-[11px] ${
                      regionShape === sh.value
                        ? "bg-blue-600 text-white"
                        : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    {sh.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        {mode === "region" && (
          <p className="text-[11px] text-slate-500 mt-2">
            Drag over anything that needs hiding — a face, a plate, an address.
            Blur softens it; mask covers it with a solid block.
          </p>
        )}

        {edit.regions.length > 0 && (
          <div className="mt-2 space-y-2">
            {edit.regions.map((r, i) => (
              <div key={i} className="border border-slate-800 rounded p-2">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-400 w-4">{i + 1}.</span>
                  <div className="flex rounded border border-slate-700 overflow-hidden">
                    {["blur", "mask"].map((m) => (
                      <button
                        key={m}
                        onClick={() => setRegion(i, { mode: m })}
                        className={`px-2 py-0.5 text-[10px] capitalize ${
                          r.mode === m
                            ? "bg-slate-700 text-white"
                            : "text-slate-500 hover:text-slate-300"
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                  <div className="flex rounded border border-slate-700 overflow-hidden">
                    {[
                      { value: "rect", label: "Box" },
                      { value: "ellipse", label: "Circle" },
                    ].map((sh) => (
                      <button
                        key={sh.value}
                        onClick={() => setRegion(i, { shape: sh.value })}
                        className={`px-2 py-0.5 text-[10px] ${
                          (r.shape || "rect") === sh.value
                            ? "bg-slate-700 text-white"
                            : "text-slate-500 hover:text-slate-300"
                        }`}
                      >
                        {sh.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex-1" />
                  <Button
                    variant="ghost" size="sm"
                    className="h-6 px-2 text-red-400"
                    onClick={() => removeRegion(i)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                {r.mode === "blur" && (
                  <Row label="Strength">
                    <Pct
                      value={r.strength ?? 60}
                      min={0}
                      max={100}
                      onChange={(v) => setRegion(i, { strength: v })}
                    />
                  </Row>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-slate-200 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center gap-3">
      <Label className="text-[11px] text-slate-400 w-20 shrink-0">{label}</Label>
      {children}
    </div>
  );
}

function Pct({ value, min, max, step = 1, onChange, suffix = "%" }) {
  return (
    <div className="flex items-center gap-2 flex-1">
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={([v]) => onChange(v)}
        className="flex-1"
      />
      <span className="text-[11px] text-slate-400 w-12 text-right tabular-nums">
        {value}{suffix}
      </span>
    </div>
  );
}

function LayerBlock({ layerKey, label, hint, value, onChange }) {
  const on = !!value.enabled;
  return (
    <div
      className={`rounded border transition-colors ${
        on ? "border-slate-700 bg-slate-950/40" : "border-slate-800"
      }`}
    >
      <label className="flex items-start gap-3 p-3 cursor-pointer">
        <Checkbox
          checked={on}
          onCheckedChange={(v) => onChange({ enabled: !!v })}
          className="mt-0.5"
        />
        <span className="min-w-0">
          <span className="block text-sm text-slate-200">{label}</span>
          <span className="block text-[11px] text-slate-500">{hint}</span>
        </span>
      </label>

      {on && (
        <div className="px-3 pb-3 space-y-2.5 border-t border-slate-800 pt-3">
          {"text" in value && (
            <Input
              value={value.text}
              onChange={(e) => onChange({ text: e.target.value })}
              placeholder="Watermark text"
              className="h-8 text-sm"
            />
          )}

          {layerKey === "corner_logo" && (
            <Row label="Corner">
              <Select
                value={value.corner}
                onValueChange={(v) => onChange({ corner: v })}
              >
                <SelectTrigger className="h-8 flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CORNERS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Row>
          )}

          {(layerKey === "custom_text" || layerKey === "full_logo") && (
            <Row label="Position">
              <Select
                value={value.position}
                onValueChange={(v) => onChange({ position: v })}
              >
                <SelectTrigger className="h-8 flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {POSITIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Row>
          )}

          <Row label="Opacity">
            <Pct
              value={Math.round(value.opacity * 100)}
              min={5}
              max={100}
              onChange={(v) => onChange({ opacity: v / 100 })}
            />
          </Row>

          {"size_pct" in value && (
            <Row label="Size">
              <Pct
                value={value.size_pct}
                min={1}
                max={20}
                step={0.5}
                onChange={(v) => onChange({ size_pct: v })}
              />
            </Row>
          )}
          {"scale_pct" in value && (
            <Row label="Size">
              {/* The full-frame logo is meant to span the picture, so it needs
                  the whole range; a corner badge that big would be absurd. */}
              <Pct
                value={value.scale_pct}
                min={layerKey === "full_logo" ? 10 : 4}
                max={layerKey === "full_logo" ? 100 : 45}
                onChange={(v) => onChange({ scale_pct: v })}
              />
            </Row>
          )}
          {"height_pct" in value && (
            <Row label="Height">
              <Pct
                value={value.height_pct}
                min={3}
                max={25}
                onChange={(v) => onChange({ height_pct: v })}
              />
            </Row>
          )}
          {"spacing_pct" in value && (
            <Row label="Spacing">
              <Pct
                value={value.spacing_pct}
                min={0}
                max={200}
                step={5}
                onChange={(v) => onChange({ spacing_pct: v })}
              />
            </Row>
          )}
          {"angle" in value && (
            <Row label="Angle">
              <Pct
                value={value.angle}
                min={-90}
                max={90}
                step={5}
                onChange={(v) => onChange({ angle: v })}
                suffix="°"
              />
            </Row>
          )}

          {/* Independent nudges, so the logo can be moved off a busy edge
              without having to shrink it. Positive is always further into the
              frame, whichever corner is selected. */}
          {"offset_x_pct" in value && (
            <>
              <Row label="Move ⟷">
                <Pct
                  value={value.offset_x_pct}
                  min={-30}
                  max={40}
                  onChange={(v) => onChange({ offset_x_pct: v })}
                />
              </Row>
              <Row label="Move ↕">
                <Pct
                  value={value.offset_y_pct}
                  min={-30}
                  max={40}
                  onChange={(v) => onChange({ offset_y_pct: v })}
                />
              </Row>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SourcePicker({
  busy, uploadPct, dragging, setDragging, onDrop, fileRef, uploadFile,
  pickFromDrive, driveUrl, setDriveUrl, fetchDriveLink,
}) {
  const driveReady = isDrivePickerConfigured();
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !busy && fileRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
          dragging
            ? "border-blue-500 bg-blue-950/20"
            : "border-slate-700 hover:border-slate-600 bg-slate-900/40"
        }`}
      >
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept="video/*,image/*"
          onChange={(e) => {
            uploadFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        {busy === "upload" ? (
          <>
            <Loader2 className="w-8 h-8 mx-auto mb-3 text-blue-400 animate-spin" />
            <p className="text-sm text-slate-300">Uploading… {uploadPct}%</p>
            <div className="h-1.5 bg-slate-800 rounded mt-3 max-w-xs mx-auto overflow-hidden">
              <div className="h-full bg-blue-600" style={{ width: `${uploadPct}%` }} />
            </div>
          </>
        ) : (
          <>
            <Upload className="w-8 h-8 mx-auto mb-3 text-slate-500" />
            <p className="text-sm text-slate-300">
              Drop a video or photo here, or click to choose one
            </p>
            <p className="text-[11px] text-slate-500 mt-1">
              MP4, MOV, WEBM, MKV · JPG, PNG, WEBP, HEIC · up to 2 GB
            </p>
          </>
        )}
      </div>

      <div className="flex items-center gap-3">
        <div className="h-px bg-slate-800 flex-1" />
        <span className="text-[11px] text-slate-500">or from Google Drive</span>
        <div className="h-px bg-slate-800 flex-1" />
      </div>

      <Button
        variant="outline"
        className="w-full"
        onClick={pickFromDrive}
        disabled={!driveReady || !!busy}
      >
        {busy === "drive" ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <HardDriveDownload className="w-4 h-4 mr-2" />
        )}
        Pick from Drive
      </Button>
      <p className="text-[11px] text-slate-500 -mt-2 px-1">
        Works on your own files, "Shared with me", and Shared Drives.
      </p>

      <div className="flex gap-2">
        <Input
          value={driveUrl}
          onChange={(e) => setDriveUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fetchDriveLink()}
          placeholder="…or paste a Drive link"
          className="flex-1"
        />
        <Button
          variant="outline"
          onClick={fetchDriveLink}
          disabled={!driveUrl.trim() || !!busy}
        >
          {busy === "link" ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Link2 className="w-4 h-4" />
          )}
        </Button>
      </div>
      <p className="text-[11px] text-slate-500 -mt-2 px-1">
        A pasted link only works if the file is shared "Anyone with the link".
        For anything private, use Pick from Drive.
      </p>
    </div>
  );
}
